<template>
  <q-chip
    square
    color="transparent"
    :size="size"
    clickable
    :class="{
      'q-px-none': no_padding,
      'q-mx-none': no_padding,
      'price-tag-positive': amount_float > 0,
      'price-tag-negative': amount_float <= 0,
      'q-ma-none': no_margin,
    }"
    @click.stop="copy"
  >
    {{ amount_formatted }}

    <template v-if="false">
      <q-icon
        name="help_outline"
        class="q-ml-sm"
      >
        <q-tooltip
          anchor="center left"
          self="center right"
          :delay="500"
        >
          {{ i18n.asset_unknown }}<br>
          {{ i18n.support_contact }}
        </q-tooltip>
      </q-icon>
    </template>

    <template v-else>
      <img
        :src="asset_icon"
        :alt="symbol"
        width="14"
        height="14"
        class="q-ml-sm"
        style="border-radius: 50%"
        @error="icon_hide"
      >
      <q-icon
        v-if="customized"
        name="timeline"
        color="primary"
        size="10px"
        style="position: absolute; right: 2px; bottom: 0px;"
      />

      <!--
      <q-tooltip
        anchor="center left"
        self="center right"
        :class="{
          'price-tag-positive-bg': amount_float > 0,
          'price-tag-positive-text': amount_float > 0,
          'price-tag-negative-bg': amount_float <= 0,
          'price-tag-negative-text': amount_float <= 0,
        }"
        :delay="500"
      >
        {{ amount }} {{ symbol }}
      </q-tooltip>
      -->

      <q-tooltip
        anchor="center left"
        self="center right"
        :delay="500"
        class="transaction-tooltip q-card shadow q-pa-md q-menu"
        max-width="fit-content"
        style="min-width: 200px"
      >
        <!-- Main asset info -->
        <q-item
          class="q-pa-none"
          dense
        >
          <q-item-section avatar>
            <img
              :src="asset_icon"
              :alt="symbol"
              width="32"
              height="32"
              style="border-radius: 50%"
              @error="icon_hide"
            >
          </q-item-section>

          <q-item-section>
            <q-item-label
              class="text-body2"
              :class="{ 'text-positive': amount > 0, 'text-primary': amount < 0 }"
            >
              {{ (amount > 0 ? '+' : '') + amount }} {{ symbol || exchange_symbol }}
            </q-item-label>

            <q-item-label
              caption
              class="text-muted"
            >
              {{ asset_name }}
            </q-item-label>
          </q-item-section>
        </q-item>

        <q-separator
          v-if="is_unknown_asset || excluded || customized"
          class="q-my-sm"
        />

        <!-- Unknown Asset Warning -->
        <q-item
          v-if="is_unknown_asset && !excluded"
          class="q-pa-none"
          dense
        >
          <q-item-section avatar>
            <q-avatar
              color="pastel-red"
              text-color="black"
              size="md"
            >
              <q-icon
                name="question_mark"
                size="16px"
              />
            </q-avatar>
          </q-item-section>
          <q-item-section>
            <q-item-label class="text-caption text-primary">
              {{ i18n('asset_unknown') }}
            </q-item-label>
          </q-item-section>
        </q-item>

        <!-- Excluded Asset Warning (Spam) -->
        <q-item
          v-if="excluded"
          class="q-pa-none"
          dense
        >
          <q-item-section avatar>
            <q-avatar
              color="pastel-red"
              text-color="black"
              size="md"
            >
              <q-icon
                name="block"
                size="16px"
              />
            </q-avatar>
          </q-item-section>
          <q-item-section>
            <q-item-label class="text-caption text-primary">
              {{ i18n('asset_excluded') }}
            </q-item-label>
          </q-item-section>
        </q-item>

        <!-- Personalised Warning -->
        <q-item
          v-if="customized"
          class="q-pa-none"
          dense
        >
          <q-item-section avatar>
            <q-avatar
              color="pastel-purple"
              text-color="black"
              size="md"
            >
              <q-icon
                name="timeline"
                size="sm"
              />
            </q-avatar>
          </q-item-section>
          <q-item-section>
            <q-item-label class="text-caption text-primary">
              {{ i18n('asset_customized') }}
            </q-item-label>
          </q-item-section>
        </q-item>
      </q-tooltip>
    </template>
  </q-chip>
</template>

<script>
import { Notify, copyToClipboard } from "quasar";
import frQuasar from "quasar/lang/fr";
import enQuasar from "quasar/lang/en-US";

export default {
  name: "AssetChip",
  props: {
    symbol: {
      type: String,
      default: null,
    },
    exchange_symbol: {
      type: String,
      default: null,
    },
    amount: {
      type: Number,
      required: true,
    },
    amount_formatted: {
      type: String,
      required: true,
    },
    size: {
      type: String,
      default: "",
    },
    no_padding: {
      type: Boolean,
      default: false,
    },
    no_margin: {
      type: Boolean,
      default: false,
    },
    asset_icon: {
      type: String,
      default: "",
    },
    asset_name: {
      type: String,
      default: "",
    },
    excluded: {
      type: Boolean,
      default: false,
    },
    customized: {
      type: Boolean,
      default: false,
    },
  },
  data() {
  },
  computed: {
    is_unknown_asset() {
      return this.symbol == null && this.exchange_symbol != null;
    },
    amount_float() {
      return parseFloat(this.amount);
    }
  },
  methods: {
    copy: async function() {
      try {
        copyToClipboard(this.amount)
          .then(() => {
            Notify.create({
              message: this.i18n("copied"),
              type: "positive",
              icon: "las la-clipboard-check",
              timeout: 1000,
            });
          })
          .catch(() => {
            Notify.create({
              message: this.i18n("copied_error"),
              type: "negative",
              icon: "info",
              timeout: 1000,
            });
          });
      } catch (error) {
        console.error('Failed to copy:', error);
      }
    },
    i18n: function(key) {
      switch (this.$q.lang.isoName) {
      case enQuasar.isoName:
        return {
          asset_unknown: "This asset is not recognized.",
          asset_customized: "This asset has a customized change rate.",
          asset_excluded: "This asset is excluded.",
          copied: "Copied to clipboard",
          copied_error: "Error copying to clipboard",
          support_contact: "Please contact support if you believe this is an error.",
        }[key];
      case frQuasar.isoName:
        return {
          asset_unknown: "Cet actif n’est pas reconnu.",
          asset_customized: "Cet actif a un taux de change personnalisé.",
          asset_excluded: "Cet actif est marqué comme indésirable.",
          copied: "Copié dans le presse-papier",
          copied_error: "Erreur lors de la copie dans le presse-papier",
          support_contact: "Veuillez contacter le support si vous pensez qu’il s’agit d’une erreur.",
        }[key];
      }
    },
    icon_hide(event) {
      event.target.style.display = "none";
    },
  },
};
</script>
